// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms under AGPL section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * DOM: builds the app shell, owns the modal + toast systems, and renders the two
 * result surfaces (the live event gallery and the file-scan segment list). Heavy
 * logic lives in the sibling modules; this wires the surfaces the user touches.
 */

import { icon } from './icons';
import type { CaptureEvent, Segment } from './types';
import { formatDuration, formatTimecode } from './gate';

export interface Refs {
  modeTabs: HTMLElement;
  livePanel: HTMLElement;
  filePanel: HTMLElement;
  // live
  video: HTMLVideoElement;
  overlay: HTMLCanvasElement;
  camOverlay: HTMLElement;
  armBig: HTMLButtonElement;
  statusChip: HTMLElement;
  recDot: HTMLElement;
  meterFill: HTMLElement;
  meterThresh: HTMLElement;
  meterVal: HTMLElement;
  armBtn: HTMLButtonElement;
  sensSlider: HTMLInputElement;
  sensLabel: HTMLElement;
  micToggle: HTMLButtonElement;
  beepToggle: HTMLButtonElement;
  notifyToggle: HTMLButtonElement;
  zoneToggle: HTMLButtonElement;
  clearZoneBtn: HTMLButtonElement;
  flipBtn: HTMLButtonElement;
  camStatus: HTMLElement;
  gallery: HTMLElement;
  galleryEmpty: HTMLElement;
  galleryCount: HTMLElement;
  clearAllBtn: HTMLButtonElement;
  sheetBtn: HTMLButtonElement;
  // file
  dropzone: HTMLElement;
  fileInput: HTMLInputElement;
  sampleBtn: HTMLButtonElement;
  dropEmpty: HTMLElement;
  fileStage: HTMLElement;
  previewVideo: HTMLVideoElement;
  scanWrap: HTMLElement;
  scanBar: HTMLElement;
  scanStatus: HTMLElement;
  segList: HTMLElement;
  segEmpty: HTMLElement;
  fileExports: HTMLElement;
  csvBtn: HTMLButtonElement;
  jsonBtn: HTMLButtonElement;
  fileSheetBtn: HTMLButtonElement;
  rescanBtn: HTMLButtonElement;
  // chrome
  drawer: HTMLElement;
  drawerToggle: HTMLButtonElement;
}

const SHELL = `
<div class="app-shell">
  <header class="topbar">
    <a class="brand" href="/" aria-label="Sentrywell home">
      <svg class="brand-mark" viewBox="0 0 32 32" aria-hidden="true">
        <circle cx="12" cy="16" r="4.5" fill="none" stroke="#38e0d0" stroke-width="2.6"/>
        <circle cx="12" cy="16" r="1.7" fill="#38e0d0"/>
        <path d="M19 11 a7 7 0 0 1 0 10" fill="none" stroke="#22b8ff" stroke-width="2.6" stroke-linecap="round"/>
        <path d="M22.5 8 a11.5 11.5 0 0 1 0 16" fill="none" stroke="#22b8ff" stroke-width="2.6" stroke-linecap="round" opacity="0.5"/>
      </svg>
      <span class="brand-name">Sentry<span class="brand-accent">well</span></span>
    </a>
    <nav class="topbar-nav">
      <button type="button" class="nav-link" data-modal="how">How it works</button>
      <button type="button" class="nav-link" data-modal="privacy">Privacy</button>
      <button type="button" class="nav-link" data-modal="about">About</button>
      <button type="button" class="nav-link icon-only" id="drawer-toggle" aria-label="Open event log" title="Event log">${icon('list')}</button>
    </nav>
  </header>

  <main class="main-content">
    <button type="button" class="trust-banner" data-modal="privacy">
      <span class="trust-dot"></span>
      Runs entirely in your browser. Your camera feed and footage never leave this device.
    </button>

    <h1 class="lede">A security camera that never phones home.</h1>
    <p class="sub">Arm your webcam and Sentrywell watches for movement, snapping a photo and recording a clip only when something changes — using <span data-term="frame differencing" class="glossary-link">frame differencing</span> right here in the tab. Or drop in a video and it finds every moment of motion. Nothing is uploaded, ever.</p>

    <div class="mode-tabs" id="mode-tabs" role="tablist">
      <button type="button" class="mode-tab active" data-mode="live" role="tab">${icon('camera')}<span>Live camera</span></button>
      <button type="button" class="mode-tab" data-mode="file" role="tab">${icon('film')}<span>Scan a video</span></button>
    </div>

    <!-- ── LIVE ────────────────────────────────────────────────────────── -->
    <section class="panel live-panel" id="live-panel">
      <div class="video-wrap">
        <div class="video-stage">
          <video id="cam-video" playsinline muted></video>
          <canvas class="overlay" id="overlay"></canvas>
          <div class="badges">
            <span class="status-chip" id="status-chip"><span class="chip-dot"></span><span class="chip-label">Idle</span></span>
            <span class="rec-badge" id="rec-dot" hidden>${icon('dot', 12)} REC</span>
          </div>
          <div class="cam-overlay" id="cam-overlay">
            <button type="button" class="big-btn" id="arm-big">${icon('power', 20)} Arm camera</button>
            <p class="cam-hint">The camera turns on in your browser only. Frames are compared and discarded; a clip is recorded only when motion is seen.</p>
          </div>
        </div>
        <div class="meter-row">
          <span class="meter-label">Motion</span>
          <div class="meter"><div class="meter-fill" id="meter-fill"></div><div class="meter-thresh" id="meter-thresh"></div></div>
          <span class="meter-val mono" id="meter-val">0%</span>
        </div>
      </div>

      <div class="controls">
        <div class="controls-main">
          <button type="button" class="btn primary big" id="arm-btn">${icon('power')} Arm camera</button>
          <span class="cam-status" id="cam-status"></span>
        </div>
        <label class="sens-field">
          <span>Sensitivity <strong id="sens-label">55</strong></span>
          <input type="range" id="sens-slider" min="1" max="100" value="55" />
        </label>
        <div class="toggle-row">
          <button type="button" class="toggle" id="beep-toggle" aria-pressed="false">${icon('volume')} Sound</button>
          <button type="button" class="toggle" id="notify-toggle" aria-pressed="false">${icon('bell')} Notify</button>
          <button type="button" class="toggle" id="mic-toggle" aria-pressed="false">${icon('mic')} Record audio</button>
          <button type="button" class="toggle" id="zone-toggle" aria-pressed="false">${icon('target')} Set zone</button>
          <button type="button" class="toggle" id="clear-zone" hidden>${icon('x')} Clear zone</button>
          <button type="button" class="toggle" id="flip-btn">${icon('refresh')} Flip</button>
        </div>
      </div>

      <div class="results">
        <div class="results-head">
          <h2>Captured events <span class="pill" id="gallery-count">0</span></h2>
          <div class="results-actions">
            <button type="button" class="btn ghost tiny" id="sheet-btn" disabled>${icon('grid')} Contact sheet</button>
            <button type="button" class="btn ghost tiny" id="clear-all" disabled>${icon('trash')} Clear all</button>
          </div>
        </div>
        <div class="gallery" id="gallery"></div>
        <div class="results-empty" id="gallery-empty">
          <p>No events yet. Arm the camera — when something moves, a snapshot and a clip will appear here, stored on this device only.</p>
        </div>
      </div>
    </section>

    <!-- ── FILE ────────────────────────────────────────────────────────── -->
    <section class="panel file-panel" id="file-panel" hidden>
      <div class="dropzone" id="dropzone" tabindex="0" role="button" aria-label="Choose a video">
        <div class="file-stage" id="file-stage" hidden>
          <video id="preview-video" controls playsinline></video>
        </div>
        <div class="dropzone-empty" id="drop-empty">
          ${icon('film', 30)}
          <p class="dz-title">Drop a video, or tap to choose</p>
          <p class="dz-sub">CCTV, dashcam, trail-cam or phone footage — Sentrywell marks every stretch of movement with a timecode. It stays on your device.</p>
          <button type="button" class="sample-link" id="sample-btn">${icon('play', 14)} Try a sample clip</button>
        </div>
        <input type="file" id="file-input" accept="video/*" hidden />
      </div>

      <div class="scan-wrap" id="scan-wrap" hidden>
        <div class="scan-status" id="scan-status">Scanning…</div>
        <div class="progress"><div class="progress-bar" id="scan-bar"></div></div>
        <button type="button" class="btn ghost tiny" id="rescan-btn">${icon('refresh')} Re-scan with current sensitivity</button>
      </div>

      <div class="results">
        <div class="results-head">
          <h2>Motion found <span class="pill" id="seg-count-host"></span></h2>
          <div class="results-actions" id="file-exports" hidden>
            <button type="button" class="btn ghost tiny" id="file-sheet-btn">${icon('grid')} Contact sheet</button>
            <button type="button" class="btn ghost tiny" id="csv-btn">${icon('download')} Timeline CSV</button>
            <button type="button" class="btn ghost tiny" id="json-btn">${icon('download')} Segments JSON</button>
          </div>
        </div>
        <div class="seg-list" id="seg-list"></div>
        <div class="results-empty" id="seg-empty">
          <p>Drop a video above to find the moments something moved.</p>
        </div>
      </div>
    </section>
  </main>

  <footer class="site-footer">
    <div class="footer-inner">
      Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
      · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>
    </div>
  </footer>

  <aside id="event-drawer" hidden></aside>
  <div id="modal-host" hidden></div>
  <div id="toast-area" aria-live="polite"></div>
</div>
`;

export function buildShell(root: HTMLElement): Refs {
  root.innerHTML = SHELL;
  const $ = <T extends HTMLElement>(sel: string): T => root.querySelector(sel) as T;
  return {
    modeTabs: $('#mode-tabs'),
    livePanel: $('#live-panel'),
    filePanel: $('#file-panel'),
    video: $('#cam-video'),
    overlay: $('#overlay'),
    camOverlay: $('#cam-overlay'),
    armBig: $('#arm-big'),
    statusChip: $('#status-chip'),
    recDot: $('#rec-dot'),
    meterFill: $('#meter-fill'),
    meterThresh: $('#meter-thresh'),
    meterVal: $('#meter-val'),
    armBtn: $('#arm-btn'),
    sensSlider: $('#sens-slider'),
    sensLabel: $('#sens-label'),
    micToggle: $('#mic-toggle'),
    beepToggle: $('#beep-toggle'),
    notifyToggle: $('#notify-toggle'),
    zoneToggle: $('#zone-toggle'),
    clearZoneBtn: $('#clear-zone'),
    flipBtn: $('#flip-btn'),
    camStatus: $('#cam-status'),
    gallery: $('#gallery'),
    galleryEmpty: $('#gallery-empty'),
    galleryCount: $('#gallery-count'),
    clearAllBtn: $('#clear-all'),
    sheetBtn: $('#sheet-btn'),
    dropzone: $('#dropzone'),
    fileInput: $('#file-input'),
    sampleBtn: $('#sample-btn'),
    dropEmpty: $('#drop-empty'),
    fileStage: $('#file-stage'),
    previewVideo: $('#preview-video'),
    scanWrap: $('#scan-wrap'),
    scanBar: $('#scan-bar'),
    scanStatus: $('#scan-status'),
    segList: $('#seg-list'),
    segEmpty: $('#seg-empty'),
    fileExports: $('#file-exports'),
    csvBtn: $('#csv-btn'),
    jsonBtn: $('#json-btn'),
    fileSheetBtn: $('#file-sheet-btn'),
    rescanBtn: $('#rescan-btn'),
    drawer: $('#event-drawer'),
    drawerToggle: $('#drawer-toggle'),
  };
}

// ── Gallery (live events) ────────────────────────────────────────────────────

export interface GalleryHandlers {
  onPlay: (ev: CaptureEvent, url: string) => void;
  onDownloadClip: (ev: CaptureEvent) => void;
  onDownloadSnap: (ev: CaptureEvent) => void;
  onShare: (ev: CaptureEvent) => void;
  onDelete: (ev: CaptureEvent) => void;
}

let galleryUrls: string[] = [];

export function renderGallery(refs: Refs, events: CaptureEvent[], canShare: boolean, handlers: GalleryHandlers): void {
  for (const u of galleryUrls) URL.revokeObjectURL(u);
  galleryUrls = [];

  refs.galleryCount.textContent = String(events.length);
  refs.clearAllBtn.disabled = events.length === 0;
  refs.sheetBtn.disabled = events.length === 0;
  refs.galleryEmpty.hidden = events.length > 0;
  refs.gallery.hidden = events.length === 0;
  refs.gallery.innerHTML = '';

  const ordered = [...events].sort((a, b) => b.ts - a.ts);
  for (const ev of ordered) {
    const thumbUrl = URL.createObjectURL(ev.snapshot);
    galleryUrls.push(thumbUrl);
    const clipUrl = ev.clip ? URL.createObjectURL(ev.clip) : '';
    if (clipUrl) galleryUrls.push(clipUrl);

    const time = new Date(ev.ts);
    const tile = document.createElement('div');
    tile.className = 'event-card';
    tile.innerHTML = `
      <button type="button" class="event-thumb" aria-label="Play event">
        <img src="${thumbUrl}" alt="Motion snapshot" />
        ${ev.clip ? `<span class="thumb-play">${icon('play', 22)}</span>` : ''}
        <span class="thumb-dur mono">${formatDuration(ev.durationMs)}</span>
      </button>
      <div class="event-meta">
        <span class="event-time mono">${time.toLocaleTimeString()}</span>
        <div class="event-btns"></div>
      </div>`;

    const playBtn = tile.querySelector('.event-thumb') as HTMLButtonElement;
    playBtn.addEventListener('click', () => handlers.onPlay(ev, clipUrl));

    const btns = tile.querySelector('.event-btns') as HTMLElement;
    btns.appendChild(iconBtn('download', ev.clip ? 'Download clip' : 'Download photo', () =>
      ev.clip ? handlers.onDownloadClip(ev) : handlers.onDownloadSnap(ev),
    ));
    if (ev.clip) btns.appendChild(iconBtn('image', 'Download photo', () => handlers.onDownloadSnap(ev)));
    if (canShare) btns.appendChild(iconBtn('share', 'Share', () => handlers.onShare(ev)));
    btns.appendChild(iconBtn('trash', 'Delete', () => handlers.onDelete(ev)));

    refs.gallery.appendChild(tile);
  }
}

// ── Segment list (file scan) ─────────────────────────────────────────────────

export interface SegView {
  seg: Segment;
  thumb: Blob;
}
export interface SegHandlers {
  onJump: (t: number) => void;
  onDownloadSnap: (view: SegView, index: number) => void;
}

let segUrls: string[] = [];

export function renderSegments(refs: Refs, views: SegView[], handlers: SegHandlers): void {
  for (const u of segUrls) URL.revokeObjectURL(u);
  segUrls = [];

  const host = document.getElementById('seg-count-host');
  if (host) host.textContent = String(views.length);
  refs.segEmpty.hidden = views.length > 0;
  refs.segList.hidden = views.length === 0;
  refs.fileExports.hidden = views.length === 0;
  refs.segList.innerHTML = '';

  views.forEach((v, i) => {
    const url = URL.createObjectURL(v.thumb);
    segUrls.push(url);
    const row = document.createElement('div');
    row.className = 'seg-row';
    row.innerHTML = `
      <button type="button" class="seg-thumb" aria-label="Jump to ${formatTimecode(v.seg.peakT)}">
        <img src="${url}" alt="Motion at ${formatTimecode(v.seg.peakT)}" />
        <span class="thumb-play">${icon('play', 20)}</span>
      </button>
      <div class="seg-info">
        <span class="seg-time mono">${formatTimecode(v.seg.startT)} – ${formatTimecode(v.seg.endT)}</span>
        <span class="seg-sub">peak ${formatTimecode(v.seg.peakT)} · ${(v.seg.endT - v.seg.startT).toFixed(1)}s · intensity ${Math.round(v.seg.peakScore * 100)}%</span>
      </div>
      <div class="seg-btns"></div>`;
    (row.querySelector('.seg-thumb') as HTMLButtonElement).addEventListener('click', () => handlers.onJump(v.seg.peakT));
    (row.querySelector('.seg-btns') as HTMLElement).appendChild(
      iconBtn('download', 'Download snapshot', () => handlers.onDownloadSnap(v, i)),
    );
    refs.segList.appendChild(row);
  });
}

export function revokeSegUrls(): void {
  for (const u of segUrls) URL.revokeObjectURL(u);
  segUrls = [];
}

function iconBtn(name: string, label: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'icon-btn';
  b.title = label;
  b.setAttribute('aria-label', label);
  b.innerHTML = icon(name, 16);
  b.addEventListener('click', onClick);
  return b;
}

// ── Modal system ─────────────────────────────────────────────────────────────

let modalHost: HTMLElement | null = null;

function host(): HTMLElement {
  if (!modalHost) modalHost = document.getElementById('modal-host');
  return modalHost as HTMLElement;
}

export function isOverlayOpen(): boolean {
  const h = host();
  const d = document.getElementById('event-drawer');
  return (h && !h.hidden) || (d ? !d.hidden : false);
}

export function openModal(title: string, body: HTMLElement | string): void {
  const h = host();
  h.innerHTML = '';
  const backdrop = document.createElement('div');
  backdrop.className = 'modal-backdrop';
  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');

  const head = document.createElement('div');
  head.className = 'modal-header';
  const h2 = document.createElement('h2');
  h2.className = 'modal-title';
  h2.textContent = title;
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'modal-close';
  close.setAttribute('aria-label', 'Close');
  close.innerHTML = icon('x');
  close.addEventListener('click', closeModal);
  head.appendChild(h2);
  head.appendChild(close);

  const content = document.createElement('div');
  content.className = 'modal-body';
  if (typeof body === 'string') content.innerHTML = body;
  else content.appendChild(body);

  modal.appendChild(head);
  modal.appendChild(content);
  backdrop.appendChild(modal);
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) closeModal();
  });
  h.appendChild(backdrop);
  h.hidden = false;
}

export function closeModal(): void {
  const h = host();
  h.hidden = true;
  h.innerHTML = '';
}

// ── Toast ────────────────────────────────────────────────────────────────────

export function toast(msg: string, kind: 'ok' | 'err' = 'ok'): void {
  const area = document.getElementById('toast-area');
  if (!area) return;
  const t = document.createElement('div');
  t.className = `toast ${kind}`;
  t.innerHTML = `${icon(kind === 'ok' ? 'check' : 'info')}<span>${escapeHtml(msg)}</span>`;
  area.appendChild(t);
  requestAnimationFrame(() => t.classList.add('show'));
  setTimeout(() => {
    t.classList.remove('show');
    setTimeout(() => t.remove(), 300);
  }, 2600);
}

// ── Static modal content ─────────────────────────────────────────────────────

export function howItWorksBody(): string {
  const step = (n: string, title: string, text: string) =>
    `<li><span class="step-num">${n}</span><div><h3>${title}</h3><p>${text}</p></div></li>`;
  return `<div class="steps-wrap"><ol class="steps">
    ${step('01', 'Arm, or drop a video', 'Point a webcam at what you want watched and arm it, or switch to “Scan a video” and drop in footage. Deny the camera and the video-scan path does the same job on a file.')}
    ${step('02', 'Frames are compared on device', 'Each frame is reduced to a coarse grid of brightness and compared with the one before via <span data-term="frame differencing" class="glossary-link">frame differencing</span>. How much must change to count as motion is the <span data-term="sensitivity" class="glossary-link">sensitivity</span>; an optional <span data-term="detection zone" class="glossary-link">detection zone</span> ignores everything outside a rectangle.')}
    ${step('03', 'It records only when it matters', 'When movement holds, Sentrywell grabs a photo and starts a <span data-term="media recorder" class="glossary-link">clip</span>, stopping once the scene settles for the <span data-term="cooldown" class="glossary-link">cooldown</span>. Events are kept in this browser’s <span data-term="indexeddb" class="glossary-link">IndexedDB</span> on your device.')}
    ${step('04', 'Leave with the footage', 'Download or share each clip and snapshot, or export a contact sheet. For scanned videos you also get a timeline CSV and a segments JSON.')}
  </ol></div>`;
}

export function privacyBody(): string {
  return `<div class="security-grid">
    <div class="security-card good">
      <h3>What's protected</h3>
      <ul>
        <li>The camera (and optional microphone) stream — frames are compared and discarded. <strong>Nothing is recorded unless motion is detected</strong>, and clips are stored only in this browser’s IndexedDB on this device.</li>
        <li>Any video you drop in — decoded and scanned in this tab; it is never uploaded.</li>
        <li>Captured events — on this device only. “Clear all” wipes them, and every camera/mic track is stopped when you disarm or leave the page.</li>
      </ul>
    </div>
    <div class="security-card bad">
      <h3>What isn't</h3>
      <ul>
        <li>The initial page load is served by GitHub Pages, whose CDN sees your IP address — the same as visiting any website.</li>
        <li>A system notification’s text (“Motion detected”) is visible on your own lock screen. It contains no footage and is sent nowhere.</li>
        <li>Detection is pixel-based. It can be fooled by light changes, and it can’t tell a person from a curtain — it is a motion trigger, not a security guarantee.</li>
        <li>While armed, monitoring continues even if the tab is in the background (that is the point); everything stops the moment you disarm or close the page.</li>
      </ul>
    </div>
    <div class="security-card">
      <h3>Trust surface</h3>
      <ul>
        <li>The static site bundle, hash-pinned by the GitHub Pages deploy, and the TLS chain between you and GitHub Pages.</li>
        <li>A Cloudflare Web Analytics beacon records anonymous page views — no cookies, no fingerprinting, no cross-site tracking; your footage is never sent to it.</li>
        <li>Feedback you choose to send is sent to feedback.benrichardson.dev. Nothing is sent unless you open the feedback form and press Send; your footage never is.</li>
      </ul>
    </div>
  </div>`;
}

export function aboutBody(): string {
  return `<div class="about">
    <p><strong>Sentrywell</strong> turns a spare browser into a motion-detecting camera that records only when something moves — and can also find the movement in a video you already have. It runs entirely on your device with no backend and no account, because home footage is the last thing that should live on someone else’s server.</p>
    <p>It’s part of a small workshop of privacy-first browser tools. See the rest at <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">lab.benrichardson.dev</a>, and more from the author at <a href="https://benrichardson.dev" target="_blank" rel="noopener">benrichardson.dev</a>.</p>
    <p class="about-src">Source: <a href="https://github.com/ben-gy/sentrywell" target="_blank" rel="noopener">github.com/ben-gy/sentrywell</a></p>
  </div>`;
}

export function playerModalBody(url: string, poster: string): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'player-modal';
  const v = document.createElement('video');
  v.src = url;
  v.controls = true;
  v.autoplay = true;
  v.setAttribute('playsinline', '');
  v.poster = poster;
  wrap.appendChild(v);
  return wrap;
}

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
